import { useState } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { sendEmail } from "@/utils/email/api";
import { renderPaymentRequestTemplate, renderPaymentRequestEmailHtml } from "@/utils/paymentTemplate";
import { loadPaymentSettings } from "@/hooks/usePaymentSettings";
import { createOrUpdateCustomer } from "@/utils/customerUtils";
import { convertBankDetails, getReviewLinkUrl } from "@/utils/paymentHelpers";
import { getDocumentMeta, type DocumentType } from "@/utils/documentMeta";
import { uploadReceiptPdf } from "@/utils/pdf/uploadReceipt";

interface EmailPaymentHandlerProps {
  form: any;
  businessProfile: any;
  watchIncludeReviewLink: boolean;
  watchIncludeAddress: boolean;
  selectedReviewPlatform: string;
  onLinkGenerated?: () => void;
}

interface SendOptions {
  paymentId?: string;
  skipPersistence?: boolean;
  documentType?: DocumentType;
  includePdf?: boolean;
  paymentMethod?: string;
  customerPaymentStatus?: string;
}

export const useEmailPaymentHandler = ({
  form,
  businessProfile,
  watchIncludeReviewLink,
  watchIncludeAddress,
  selectedReviewPlatform,
  onLinkGenerated,
}: EmailPaymentHandlerProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const handleSendPaymentEmail = async (options: SendOptions = {}) => {
    const docType: DocumentType = options.documentType ?? "invoice";
    const meta = getDocumentMeta(docType);
    setIsSendingEmail(true);
    try {
      const data = form.getValues();
      if (!data.customerEmail) {
        toast({
          title: "Error",
          description: "Please provide a customer email to send by email",
          variant: "destructive",
        });
        return;
      }

      const amount = parseFloat(data.amount || "0");
      const description = data.description || "";
      const currency = data.currency || "GBP";
      const businessName = businessProfile?.businessName || "Our Business";
      const paymentId = options.paymentId ?? crypto.randomUUID();
      const formattedBankDetails = convertBankDetails(businessProfile?.bankDetails);

      let reviewLinkUrl: string | undefined;
      if (watchIncludeReviewLink && businessProfile) {
        const platformToUse = selectedReviewPlatform || businessProfile.primary_review_link;
        reviewLinkUrl = getReviewLinkUrl(platformToUse, businessProfile);
      }

      // Compute due date only when the user configured a non-zero offset.
      const pdfSettings = loadPaymentSettings(user?.id);
      let computedDueDate: string | undefined;
      if (docType === "invoice" && (pdfSettings.paymentDueDays ?? 0) > 0) {
        const d = new Date();
        d.setDate(d.getDate() + Number(pdfSettings.paymentDueDays));
        computedDueDate = d.toLocaleDateString("en-GB");
      }

      // Always generate the PDF so we can upload it to the receipts bucket
      // (used by the "View Invoice/Receipt/Estimate" button on the customer card).
      // The `includePdf` flag only controls whether to attach it to the email.
      const includePdf = options.includePdf ?? true;
      const attachments: any[] = [];
      try {
        const pdfBase64 = await meta.generatePdf({
          receiptNumber: Math.floor(Math.random() * 100000).toString(),
          date: new Date().toLocaleDateString("en-GB"),
          amount,
          currency,
          description,
          customerName: data.customerName,
          customerEmail: data.customerEmail,
          customerPhone: data.customerPhone,
          customerAddress: watchIncludeAddress
            ? { street: data.street, city: data.city, postcode: data.postcode, country: data.country }
            : undefined,
          businessProfile,
          bankDetails: formattedBankDetails,
          includeAddress: watchIncludeAddress,
          paymentMethod: options.paymentMethod,
          dueDate: computedDueDate,
          receiptSettings: pdfSettings,
        });
        if (includePdf) {
          attachments.push({
            filename: `${docType}-${paymentId}.pdf`,
            content: pdfBase64.split(",")[1],
            type: "application/pdf",
          });
        }
        if (user?.id) {
          try {
            await uploadReceiptPdf(pdfBase64.split(",")[1], user.id, paymentId);
          } catch (uploadErr) {
            console.error("Failed to upload PDF to bucket:", uploadErr);
          }
        }
      } catch (err) {
        console.error("Error generating PDF:", err);
      }


      const savedSettings = loadPaymentSettings(user?.id);
      const template = meta.template(savedSettings);
      const messageBody = renderPaymentRequestTemplate(template, {
        customerName: data.customerName,
        businessName,
        amount,
        currency,
        description,
        accountName: formattedBankDetails.accountName,
        accountNumber: formattedBankDetails.accountNumber,
        sortCode: formattedBankDetails.sortCode,
        dueDays: savedSettings.paymentDueDays,
      });

      // Attach review link on initial payment request, or on cash receipts (which bypass the request)
      const allowReviewLink = docType === "invoice" || docType === "receipt";
      const bodyWithReview = (allowReviewLink && reviewLinkUrl)
        ? `${messageBody}\n\nLeave us a review: ${reviewLinkUrl}`
        : messageBody;

      const subject = `${meta.subjectPrefix} From ${businessName}`;
      const emailHTML = renderPaymentRequestEmailHtml(bodyWithReview, {
        logoUrl: savedSettings.includeLogo ? businessProfile?.logoUrl : undefined,
        businessName,
      });

      const safeFromName = (businessName || "Notifications").replace(/["<>,]/g, "").trim() || "Notifications";

      const emailResult = await sendEmail({
        to: data.customerEmail,
        subject,
        html: emailHTML,
        from: `${safeFromName} <notifications@houndpayments.co.uk>`,
        userId: user?.id,
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      if (!emailResult.success) throw new Error(emailResult.error || "Failed to send email");

      if (!options.skipPersistence) {
        const { error: paymentError } = await supabase.from("payment_links").insert([{
          id: paymentId,
          user_id: user?.id,
          amount,
          currency,
          status: meta.status,
          description,
          customer_email: data.customerEmail,
          customer_name: data.customerName || null,
          payment_type: meta.paymentType,
          review_link_included: watchIncludeReviewLink,
          provider: meta.provider,
          url: "#",
        }]);
        if (paymentError) console.error("Error creating payment record:", paymentError);

        if (data.customerName) {
          await createOrUpdateCustomer(
            data.customerEmail,
            data.customerName,
            paymentId,
            user?.id,
            watchIncludeAddress ? data.street : undefined,
            watchIncludeAddress ? data.postcode : undefined,
            data.customerPhone || undefined,
            options.customerPaymentStatus,
            watchIncludeAddress ? data.city : undefined,
          );
        }

        toast({ title: "Success", description: `${meta.label} sent via email to ${data.customerEmail}` });

        form.reset({
          amount: "", currency: "GBP", description: "",
          customerEmail: "", customerPhone: "", customerName: "",
          includeAddress: false, includePdfReceipt: false,
          includeReviewLink: false, selectedReviewLink: "",
          street: "", city: "", postcode: "", country: "",
        });
        onLinkGenerated?.();
      }
    } catch (error) {
      console.error("Error sending email:", error);
      toast({
        title: "Error",
        description: `Failed to send email: ${error instanceof Error ? error.message : "Unknown error"}`,
        variant: "destructive",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  return { handleSendPaymentEmail, isSendingEmail };
};
